
import { useState, useEffect } from "react";
import { VacationCard } from "@/components/VacationCard";
import { VacationCardSkeleton } from "@/components/VacationCardSkeleton";
import { ScrollToTop } from "@/components/ScrollToTop";
import { User } from "@supabase/supabase-js";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { LocalFavoritesContainer } from "@/components/local-favorites/LocalFavoritesContainer";
import { ReisaanbiederHeader } from "@/components/reisaanbieder/ReisaanbiederHeader";
import { ReisaanbiederFilters } from "@/components/reisaanbieder/ReisaanbiederFilters";
import { useReisaanbiederData } from "@/hooks/useReisaanbiederData";

interface ReisaanbiederPageProps {
  user?: User | null;
  favorites?: number[];
  onFavoriteChange?: () => void;
}

export const ReisaanbiederPage = ({ user = null, favorites = [], onFavoriteChange = () => {} }: ReisaanbiederPageProps) => {
  const [displayCount, setDisplayCount] = useState(20);
  const navigate = useNavigate();
  
  const {
    vacations,
    loading,
    searchTerm,
    setSearchTerm,
    selectedProvider,
    setSelectedProvider,
    priceFilter,
    setPriceFilter,
    providers
  } = useReisaanbiederData();

  const hasMoreResults = displayCount < vacations.length;
  const displayedVacations = vacations.slice(0, displayCount);

  const { shouldLoadMore, resetLoadMore } = useInfiniteScroll({
    hasNextPage: hasMoreResults,
    isLoading: loading
  });

  useEffect(() => {
    if (shouldLoadMore) {
      setDisplayCount(prev => Math.min(prev + 20, vacations.length));
      resetLoadMore();
    }
  }, [shouldLoadMore, vacations.length, resetLoadMore]);

  const handleShowAllVacations = () => {
    navigate('/alle-vakanties');
  };

  const handleShowDeals = () => {
    navigate('/deals');
  };

  const handleShowLuxury = () => {
    navigate('/luxe');
  };

  const handleShowRondreizen = () => {
    navigate('/rondreizen');
  };

  const handleShowThema = () => {
    navigate('/thema');
  };

  const handleShowFavoritesComparison = () => {
    navigate('/favorieten-vergelijken');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleBackToThemas = () => {
    navigate('/thema');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex flex-col">
      <Header 
        onShowAllVacations={handleShowAllVacations}
        onShowDeals={handleShowDeals}
        onShowLuxury={handleShowLuxury}
        onShowRondreizen={handleShowRondreizen}
        onShowThema={handleShowThema}
        onShowFavoritesComparison={handleShowFavoritesComparison}
        onGoHome={handleGoHome}
        currentView="thema"
      />
      
      <main className="flex-1">
        <div className="container mx-auto px-4 py-8">
          <div className="space-y-6">
            <ReisaanbiederHeader onBackClick={handleBackToThemas} />
            
            <ReisaanbiederFilters
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              selectedProvider={selectedProvider}
              setSelectedProvider={setSelectedProvider}
              priceFilter={priceFilter}
              setPriceFilter={setPriceFilter}
              providers={providers}
            />
            
            {loading ? (
              <>
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Vakanties laden...</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {Array.from({ length: 9 }).map((_, index) => (
                    <VacationCardSkeleton key={`skeleton-${index}`} />
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="text-sm text-gray-600">
                  {displayedVacations.length} van {vacations.length} {vacations.length === 1 ? 'vakantie' : 'vakanties'} getoond
                  {selectedProvider !== 'all' && ` van ${selectedProvider}`}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {displayedVacations.map((vacation) => (
                    <VacationCard
                      key={vacation['product ID']}
                      vacation={vacation}
                      user={user}
                      isFavorite={favorites.includes(vacation['product ID'])}
                      onFavoriteChange={onFavoriteChange}
                    />
                  ))}
                </div>

                {hasMoreResults && (
                  <div className="text-center text-sm text-gray-500 py-4">
                    Scroll naar beneden voor meer vakanties...
                  </div>
                )}

                <ScrollToTop />
              </>
            )}
          </div>
        </div>
      </main>
      
      <LocalFavoritesContainer />
      <Footer />
    </div>
  );
};

export default ReisaanbiederPage;
